"use strict";
// Drives the review form.
//
// Two ways in, told apart by the query string:
//   ?token=…          a signed invitation for one product.
//   ?order=…&key=…    the link from Shopify's own delivery email, which
//                     Flix Identity exchanges for one invitation per
//                     product on the order.
//
// Nothing here decides whether a review is allowed. The token is checked
// server-side on submit, and it is checked again there even though this
// file asked about it first — a form that trusted its own answer would be
// a form anyone could skip.
(function () {
  var root = document.querySelector("[data-flix-review-form]");
  if (!root) return;

  var form = root.querySelector("[data-flix-review-fields]");
  var invalid = root.querySelector("[data-flix-review-invalid]");
  var done = root.querySelector("[data-flix-review-done]");
  var errorBox = root.querySelector("[data-flix-review-error]");
  var picker = root.querySelector("[data-flix-review-products]");
  var productCard = root.querySelector("[data-flix-review-product]");
  var productTitle = root.querySelector("[data-flix-review-product-title]");
  var productImage = root.querySelector("[data-flix-review-product-image]");
  var submitButton = root.querySelector("[data-flix-review-submit]");

  // One product at a time: whichever the reader is currently reviewing.
  var current = null;

  function apiBase() {
    var config = window.flixIdentityConfig;
    if (!config || !config.apiBaseUrl) return null;
    return String(config.apiBaseUrl).replace(/\/+$/, "");
  }

  function show(node) {
    if (node) node.hidden = false;
  }

  function hide(node) {
    if (node) node.hidden = true;
  }

  function showInvalid() {
    hide(form);
    hide(done);
    show(invalid);
  }

  function showError(message) {
    if (!errorBox) return;
    errorBox.textContent = message;
    errorBox.hidden = false;
  }

  function clearError() {
    if (errorBox) errorBox.hidden = true;
  }

  // ── the star row ────────────────────────────────────────────────────────
  // Radios carry the value; this only paints them, so the form still works
  // if the paint never runs.
  function paintStars(value) {
    for (var star = 1; star <= 5; star++) {
      var node = root.querySelector('[data-flix-review-star="' + star + '"]');
      if (node) node.style.color = star <= value ? "var(--accent)" : "var(--border)";
    }
  }

  root.querySelectorAll('input[name="rating"]').forEach(function (input) {
    input.addEventListener("change", function () {
      paintStars(Number(input.value));
      clearError();
    });
  });

  // Hovering previews the rating, and leaving puts back whatever is chosen
  // — without the second half, the stars keep whatever the pointer last
  // brushed past, which reads as a rating the person never gave.
  root.querySelectorAll("[data-flix-review-star]").forEach(function (node) {
    var value = Number(node.dataset.flixReviewStar);
    node.parentElement.addEventListener("mouseenter", function () {
      paintStars(value);
    });
  });
  var ratingRow = root.querySelector("[data-flix-review-rating]");
  if (ratingRow) {
    ratingRow.addEventListener("mouseleave", function () {
      var chosen = root.querySelector('input[name="rating"]:checked');
      paintStars(chosen ? Number(chosen.value) : 0);
    });
  }

  // ── which product ───────────────────────────────────────────────────────
  function selectProduct(product) {
    current = product;
    if (productTitle) productTitle.textContent = product.title || "";
    if (productImage) {
      if (product.imageUrl) {
        productImage.src = product.imageUrl;
        productImage.hidden = false;
      } else {
        productImage.hidden = true;
      }
    }
    if (picker) {
      picker.querySelectorAll("button").forEach(function (button) {
        var chosen = button.dataset.productId === product.productId;
        button.style.borderColor = chosen ? "var(--accent)" : "var(--border)";
        button.setAttribute("aria-pressed", chosen ? "true" : "false");
      });
    }
  }

  function renderPicker(products) {
    if (!picker) return;
    picker.textContent = "";

    products.forEach(function (product) {
      var button = document.createElement("button");
      button.type = "button";
      button.dataset.productId = product.productId;
      button.setAttribute(
        "style",
        "display:flex;align-items:center;gap:12px;padding:12px 14px;border-radius:14px;background:var(--surface);border:1px solid var(--border);color:var(--text);cursor:pointer;text-align:start;font-family:inherit"
      );

      if (product.imageUrl) {
        var image = document.createElement("img");
        image.src = product.imageUrl;
        image.alt = "";
        image.width = 42;
        image.height = 42;
        image.setAttribute("style", "flex:none;width:42px;height:42px;border-radius:10px;object-fit:cover");
        button.appendChild(image);
      }

      var label = document.createElement("span");
      label.setAttribute("style", "font-family:var(--display);font-weight:800;font-size:14.5px;line-height:1.5");
      label.textContent = product.title || "";
      button.appendChild(label);

      button.addEventListener("click", function () {
        selectProduct(product);
      });

      picker.appendChild(button);
    });

    picker.hidden = false;
    // With a choice on screen, the single-product card would only repeat
    // whichever one is selected.
    hide(productCard);
  }

  // ── entry ───────────────────────────────────────────────────────────────
  // A star clicked in the reminder email arrives as ?rating=N: choose it, so
  // the reader does not have to pick the same star twice. Only once the
  // link has checked out — never on a page that is about to say "invalid".
  function presetRating() {
    var value = Number(new URLSearchParams(window.location.search).get("rating"));
    if (!(value >= 1 && value <= 5)) return;
    var input = root.querySelector('input[name="rating"][value="' + value + '"]');
    if (!input) return;
    input.checked = true;
    paintStars(value);
  }

  async function start() {
    var base = apiBase();
    if (!base) {
      showInvalid();
      return;
    }

    var params = new URLSearchParams(window.location.search);
    var token = params.get("token");
    var order = params.get("order");
    var key = params.get("key");

    if (token) {
      // A direct invitation. Confirm it resolves before the reader types
      // anything — being told the link is dead AFTER writing a review is
      // how someone decides never to write another.
      var check;
      try {
        check = await fetch(base + "/reviews/invitation?token=" + encodeURIComponent(token), {
          headers: { Accept: "application/json" },
        });
      } catch (e) {
        showInvalid();
        return;
      }
      if (!check.ok) {
        showInvalid();
        return;
      }
      var invitation = await check.json();
      // Title, handle and picture come from the service, which read them
      // off the order — never from the query string, which the reader can
      // edit and the form would then read back to them as fact.
      selectProduct({
        productId: invitation.productId,
        handle: invitation.handle || "",
        title: invitation.title || "",
        imageUrl: invitation.imageUrl || null,
        token: token,
      });
      presetRating();
      return;
    }

    if (order && key) {
      var response;
      try {
        response = await fetch(
          base + "/reviews/start?order=" + encodeURIComponent(order) + "&key=" + encodeURIComponent(key),
          { headers: { Accept: "application/json" } }
        );
      } catch (e) {
        showInvalid();
        return;
      }
      if (!response.ok) {
        showInvalid();
        return;
      }

      var payload = await response.json();
      if (!payload || !payload.products || payload.products.length === 0) {
        showInvalid();
        return;
      }

      if (payload.products.length === 1) {
        selectProduct(payload.products[0]);
      } else {
        renderPicker(payload.products);
        selectProduct(payload.products[0]);
      }
      presetRating();
      return;
    }

    // No link at all — somebody reached the page directly.
    showInvalid();
  }

  // ── submitting ──────────────────────────────────────────────────────────
  if (form) {
    form.addEventListener("submit", async function (event) {
      event.preventDefault();
      clearError();

      if (!current) {
        showInvalid();
        return;
      }

      var base = apiBase();
      if (!base) {
        showInvalid();
        return;
      }

      var data = new FormData(form);
      var rating = Number(data.get("rating"));
      var body = String(data.get("body") || "").trim();

      if (!rating) {
        showError(root.dataset.errorRating || "Choose a star rating first.");
        return;
      }
      if (!body) {
        showError(root.dataset.errorBody || "Write your review first.");
        return;
      }

      if (submitButton) submitButton.disabled = true;

      var response;
      try {
        response = await fetch(base + "/reviews", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            token: current.token,
            productId: current.productId,
            productHandle: current.handle || "",
            authorName: String(data.get("authorName") || ""),
            rating: rating,
            title: String(data.get("title") || "") || null,
            body: body,
          }),
        });
      } catch (e) {
        if (submitButton) submitButton.disabled = false;
        showError(root.dataset.errorNetwork || "Could not reach the service. Try again shortly.");
        return;
      }

      if (submitButton) submitButton.disabled = false;

      if (response.status === 403) {
        showInvalid();
        return;
      }
      if (!response.ok) {
        showError(root.dataset.errorGeneric || "Could not save your review. Try again.");
        return;
      }

      hide(form);
      hide(invalid);
      show(done);
    });
  }

  start();
})();
